import { useEffect, useRef, useState } from 'react';

import type { CollabConfig } from './types';
import {
  type CollabState,
  type SyncedHelpers,
  type UseCollaborationResult,
  clearIndexedDBCacheForRoom,
  hasIndexedDBStorageHeadroom,
  createCollabResources,
  destroyCollabResources,
  getAccessibleColor,
  useCleanupErrorSuppression,
  useLatestRef,
  wireProviderEvents,
} from './collaboration';

// Re-export types for consumers
export type { CollabState, SyncedHelpers };

export function useHocusPocusCollaboration(config?: CollabConfig): UseCollaborationResult {
  const { url, room, token, user, onConnectionChange, onSynced, onAwarenessChange, onAuthenticationFailed } = config ?? {};
  const [collabState, setCollabState] = useState<CollabState | null>(null);
  const [cacheChecked, setCacheChecked] = useState(false);
  // False when pre-connect clear timed out/failed — Phase 2 must not remount over stale IDB.
  const [useIndexedDB, setUseIndexedDB] = useState(true);
  // Track which room was cache-checked (ref for synchronous updates across effects)
  const cacheCheckedRoomRef = useRef<string | null>(null);

  // Stable refs to avoid recreating provider on reference changes
  const userRef = useLatestRef(user);
  const onSyncedRef = useLatestRef(onSynced);
  const onConnectionRef = useLatestRef(onConnectionChange);
  const onAwarenessRef = useLatestRef(onAwarenessChange);
  const onAuthenticationFailedRef = useLatestRef(onAuthenticationFailed);

  // Refs to be set by the editor with helper functions
  const replaceContentRef = useRef<((markdown: string) => void) | null>(null);
  const refreshHeadingsRef = useRef<(() => void) | null>(null);

  // Track sync status with refs (not state) to call onSynced directly
  const syncRefs = {
    local: useRef(false),
    remote: useRef(false),
    called: useRef(false),
    onSynced: onSyncedRef,
    onConnection: onConnectionRef,
    onAwareness: onAwarenessRef,
    onAuthenticationFailed: onAuthenticationFailedRef,
    replaceContent: replaceContentRef,
    refreshHeadings: refreshHeadingsRef,
  };

  const isCollabEnabled = !!(url && room);
  const markCleaningUp = useCleanupErrorSuppression();

  // Phase 1: Clear stale IndexedDB cache before connecting (server is authoritative).
  useEffect(() => {
    if (!isCollabEnabled || !room || !url) {
      cacheCheckedRoomRef.current = null;
      setCacheChecked(false);
      setUseIndexedDB(true);

      return;
    }

    let cancelled = false;

    const prepareRoom = async () => {
      const hasHeadroom = await hasIndexedDBStorageHeadroom();
      if (!hasHeadroom) {
        if (!cancelled) {
          cacheCheckedRoomRef.current = room;
          setUseIndexedDB(false);
          setCacheChecked(true);
        }

        return;
      }

      const cleared = await clearIndexedDBCacheForRoom(room);
      if (!cancelled) {
        cacheCheckedRoomRef.current = room;
        setUseIndexedDB(cleared);
        setCacheChecked(true);
      }
    };

    void prepareRoom();

    return () => {
      cancelled = true;
      cacheCheckedRoomRef.current = null;
      setCacheChecked(false);
      setUseIndexedDB(true);
    };
  }, [room, url, isCollabEnabled]);

  // Phase 2: Set up collaboration provider (only after cache prep completes for THIS room)
  useEffect(() => {
    if (!isCollabEnabled || !url || !room || !cacheChecked || cacheCheckedRoomRef.current !== room) {
      setCollabState(null);

      return;
    }

    const { ydoc, persistence, provider } = createCollabResources(room, url, token, { useIndexedDB });

    let mounted = true;
    let editorMounted = false;

    const mountEditor = () => {
      if (!mounted || editorMounted) return;
      editorMounted = true;
      setCollabState({ ydoc, provider, persistence, awareness: provider.awareness! });
    };

    // Mount on synced OR the same connected fallback that unlocks onSynced —
    // otherwise BrainfishEditor can stay on "Connecting..." forever if synced never fires.
    const clearSyncTimeout = wireProviderEvents(provider, persistence, syncRefs, mountEditor);
    if (provider.synced) {
      mountEditor();
    }

    // Drop the websocket while the tab is hidden (sets shouldConnect=false so Hocuspocus
    // won't auto-reconnect); reconnect when the tab is visible again.
    const syncConnection = () => {
      if (document.hidden) {
        provider.disconnect();
      } else {
        void provider.connect();
      }
    };

    document.addEventListener('visibilitychange', syncConnection);

    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', syncConnection);
      markCleaningUp();
      clearSyncTimeout();
      setCollabState(null);
      syncRefs.local.current = syncRefs.remote.current = syncRefs.called.current = false;
      destroyCollabResources(provider, persistence, ydoc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, room, token, isCollabEnabled, cacheChecked, useIndexedDB, markCleaningUp]);

  // Update awareness when user properties change (separate from provider lifecycle)
  useEffect(() => {
    const currentUser = userRef.current;
    if (collabState?.awareness && currentUser) {
      collabState.awareness.setLocalStateField('user', { ...currentUser, color: getAccessibleColor() });
    }
  }, [collabState?.awareness, user?.id, user?.name, userRef]);

  return { collabState, isCollabEnabled, replaceContentRef, refreshHeadingsRef };
}
